// 114 — UCI Neonatal (Fase 4, Tanda 6a, ver PLAN.md "FASE 4 — spec completa"
// → "114 — Bitácora UCI Neonatal"). Migrado del motor genérico compartido
// `cuidadoIntensivo.tsx` a un componente propio — mismo criterio que 113
// (footer real confirmado "SNS-MSP/HCU-form.114/2021", misma familia MSP,
// mismo header `EncabezadoMspCampos`/`useDatosEncabezadoMsp`, modo PDF
// 'msp'). Alcance de esta tanda descrito en `bloques114Datos.ts`.
//
// FHIR: Encounter class=ACUTE + extensión de nivel de cuidado intensivo
// (mecanismo compartido, preservado de `cuidadoIntensivo.tsx`), 1
// Condition si el diagnóstico de ingreso trae texto (el papel imprime un
// solo campo, no 5 filas como 113), y FC/FR/Temperatura/SatO2 + presión
// arterial (panel con 2 component, mismo patrón que Formulario020) crean
// Observation reusando los LOINC de `vitalSigns.ts`. Ver `bloques114Datos.ts`
// para la razón de por qué tensión arterial MEDIA queda solo como `Par[]`.
import { useMedplum } from '@medplum/react-hooks';
import type { Observation } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloques114 } from './Bloques114';
import { ProfesionalPorTurno } from './ProfesionalPorTurno';
import { CAMPOS_PROFESIONAL_114 } from './profesionalPorTurnoDatos';
import { diagramasGraficoConstantesUciDeEstado114, estadoInicialBitacora114, paresDeBitacora114 } from './bloques114Datos';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { EXT_NIVEL_CUIDADO_INTENSIVO } from './cuidadoIntensivo';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';

const CODIGO = '114';
const NIVEL_CUIDADO_INTENSIVO = 'UCI Neonatal';

function numeroOIndefinido(texto: string): number | undefined {
  const valor = texto.trim() === '' ? undefined : Number(texto);
  return valor !== undefined && !Number.isNaN(valor) ? valor : undefined;
}

export function Formulario114({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.114');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: NIVEL_CUIDADO_INTENSIVO });

  const [bitacora, setBitacora] = useState(() => estadoInicialBitacora114());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const pares: Par[] = paresDeBitacora114(t, bitacora);
      const diagramasGraficoConstantesUci = diagramasGraficoConstantesUciDeEstado114(bitacora.graficoTemperatura, bitacora.graficoPaPulsoFr);
      const ahora = new Date().toISOString();

      if (encounter) {
        const actual = await medplum.readReference(encounter);
        const otrasExtensiones = (actual.extension ?? []).filter((e) => e.url !== EXT_NIVEL_CUIDADO_INTENSIVO);
        lote.actualizarEncounterAbierto({
          ...actual,
          class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'ACUTE' },
          extension: [...otrasExtensiones, { url: EXT_NIVEL_CUIDADO_INTENSIVO, valueString: NIVEL_CUIDADO_INTENSIVO }],
        });
      }

      if (bitacora.diagnostico.texto.trim() !== '') {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(bitacora.diagnostico), recordedDate: ahora });
      }

      const observaciones: Observation[] = [];
      const signosDeIngreso = [
        { claveSigno: 'fc', campo: 'fc' },
        { claveSigno: 'fr', campo: 'fr' },
        { claveSigno: 'temperatura', campo: 'temperatura' },
        { claveSigno: 'sato2', campo: 'saturacionOxigeno' },
      ] as const;
      for (const { claveSigno, campo } of signosDeIngreso) {
        const valor = numeroOIndefinido(bitacora.constantesIngreso[campo]);
        if (valor === undefined) continue;
        const signo = signoVitalPorClave(claveSigno);
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }

      const taSistolica = numeroOIndefinido(bitacora.constantesIngreso.taSistolica);
      const taDiastolica = numeroOIndefinido(bitacora.constantesIngreso.taDiastolica);
      if (taSistolica !== undefined && taDiastolica !== undefined) {
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: PA_PANEL_LOINC, display: 'Presión arterial' }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            component: [
              { code: { coding: [{ system: 'http://loinc.org', code: PA_SISTOLICA_LOINC, display: 'Sistólica' }] }, valueQuantity: { value: taSistolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
              { code: { coding: [{ system: 'http://loinc.org', code: PA_DIASTOLICA_LOINC, display: 'Diastólica' }] }, valueQuantity: { value: taDiastolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
            ],
          }),
        );
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, diagramasGraficoConstantesUci);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques114
        constantesIngreso={bitacora.constantesIngreso}
        onChangeConstantesIngreso={(v) => setBitacora({ ...bitacora, constantesIngreso: v })}
        datosBitacora={bitacora.datosBitacora}
        onChangeDatosBitacora={(v) => setBitacora({ ...bitacora, datosBitacora: v })}
        diagnostico={bitacora.diagnostico}
        onChangeDiagnostico={(v) => setBitacora({ ...bitacora, diagnostico: v })}
        graficoTemperatura={bitacora.graficoTemperatura}
        onChangeGraficoTemperatura={(v) => setBitacora({ ...bitacora, graficoTemperatura: v })}
        graficoPaPulsoFr={bitacora.graficoPaPulsoFr}
        onChangeGraficoPaPulsoFr={(v) => setBitacora({ ...bitacora, graficoPaPulsoFr: v })}
        grupos={bitacora.grupos}
        onChangeGrupos={(v) => setBitacora({ ...bitacora, grupos: v })}
        ingestaIngresos={bitacora.ingestaIngresos}
        onChangeIngestaIngresos={(v) => setBitacora({ ...bitacora, ingestaIngresos: v })}
        ingestaEgresos={bitacora.ingestaEgresos}
        onChangeIngestaEgresos={(v) => setBitacora({ ...bitacora, ingestaEgresos: v })}
        balanceHidrico={bitacora.balanceHidrico}
        onChangeBalanceHidrico={(v) => setBitacora({ ...bitacora, balanceHidrico: v })}
        gasometria={bitacora.gasometria}
        onChangeGasometria={(v) => setBitacora({ ...bitacora, gasometria: v })}
        ventilacion={bitacora.ventilacion}
        onChangeVentilacion={(v) => setBitacora({ ...bitacora, ventilacion: v })}
        procedimientos={bitacora.procedimientos}
        onChangeProcedimientos={(v) => setBitacora({ ...bitacora, procedimientos: v })}
        procedimientosEnfermeria={bitacora.procedimientosEnfermeria}
        onChangeProcedimientosEnfermeria={(v) => setBitacora({ ...bitacora, procedimientosEnfermeria: v })}
        procedimientosEnfermeriaHoras={bitacora.procedimientosEnfermeriaHoras}
        onChangeProcedimientosEnfermeriaHoras={(v) => setBitacora({ ...bitacora, procedimientosEnfermeriaHoras: v })}
        procedimientosOtros={bitacora.procedimientosOtros}
        onChangeProcedimientosOtros={(v) => setBitacora({ ...bitacora, procedimientosOtros: v })}
        solicitudExamenes={bitacora.solicitudExamenes}
        onChangeSolicitudExamenes={(v) => setBitacora({ ...bitacora, solicitudExamenes: v })}
        interconsultas={bitacora.interconsultas}
        onChangeInterconsultas={(v) => setBitacora({ ...bitacora, interconsultas: v })}
        informeEnfermeria={bitacora.informeEnfermeria}
        onChangeInformeEnfermeria={(v) => setBitacora({ ...bitacora, informeEnfermeria: v })}
      />

      <ProfesionalPorTurno
        prefijoI18n="formularios.f114.profesionalTurnos"
        prefijoId="f114-prof"
        campos={CAMPOS_PROFESIONAL_114}
        estado={bitacora.profesionalTurnos}
        onChange={(v) => setBitacora({ ...bitacora, profesionalTurnos: v })}
      />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
